import React from 'react'
import { Button } from 'components/ui/button'
import { Select } from 'components/ui/select'
import { QuarterSelector } from './QuarterSelector'
import type { Quarter } from '../types/financials.types'

export interface FinancialsHeaderProps {
	storeName: string
	storeId: string
	quarters: Quarter[]
	selectedQuarterId: string
	onQuarterChange: (quarterId: string) => void
	fiscalYearStart: number
	onFiscalYearStartChange: (month: number) => void
	columnCount: number
	onColumnCountChange: (count: number) => void
	onAddQuarter: () => void
	onAddLineItem: () => void
	onUpload: () => void
	onExport: () => void
	lastSavedAt?: Date
	lastSavedBy?: string | { _id: string; name: string; email: string }
	isReadOnly?: boolean
	viewMode: 'grid' | 'chart'
	onViewModeChange: (mode: 'grid' | 'chart') => void
}

export const FinancialsHeader: React.FC<FinancialsHeaderProps> = ({
	storeName,
	storeId,
	quarters,
	selectedQuarterId,
	onQuarterChange,
	fiscalYearStart,
	onFiscalYearStartChange,
	columnCount,
	onColumnCountChange,
	onAddQuarter,
	onAddLineItem,
	onUpload,
	onExport,
	lastSavedAt,
	lastSavedBy,
	isReadOnly = false,
	viewMode,
	onViewModeChange,
}) => {
	return (
		<div className="space-y-4">
			{/* Title Row */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Site Financials</h1>
					<p className="text-sm text-gray-600 mt-1">
						Review quarterly performance and keep income statements up to date.
					</p>
				</div>
				{lastSavedAt && (
					<div className="text-right text-sm text-gray-500">
						<div className="flex items-center justify-center space-x-1">
							<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
								<path
									fillRule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
									clipRule="evenodd"
								/>
							</svg>
							{/* <span>Auto-saved {formatTimeAgo(lastSavedAt)}</span> */}
							<span>Auto-saved</span>
						</div>
						{lastSavedBy && (
							<div className="text-xs">
								By: {typeof lastSavedBy === 'string' ? lastSavedBy : lastSavedBy.name}
							</div>
						)}
					</div>
				)}

				{/* View Toggle */}
				<div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-0.5">
					<button
						onClick={() => onViewModeChange('grid')}
						className={`flex items-center space-x-1 px-2.5 py-1 rounded-md transition-all text-xs ${
							viewMode === 'grid'
								? 'bg-white text-[#0080fe] shadow-sm'
								: 'text-blue-400 hover:text-[#0080fe]'
						}`}
						title="Grid View"
					>
						<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
							<path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
						</svg>
						<span className="font-medium">Grid</span>
					</button>
					<button
						onClick={() => onViewModeChange('chart')}
						className={`flex items-center space-x-1 px-2.5 py-1 rounded-md transition-all text-xs ${
							viewMode === 'chart'
								? 'bg-white text-[#0080fe] shadow-sm'
								: 'text-blue-400 hover:text-[#0080fe]'
						}`}
						title="Chart View"
					>
						<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
							<path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
						</svg>
						<span className="font-medium">Chart</span>
					</button>
				</div>
			</div>

			{/* Controls Row */}
			<div className="flex items-center justify-between">
				<div className="flex items-center space-x-4">
					<QuarterSelector
						quarters={quarters}
						selectedQuarterId={selectedQuarterId}
						onQuarterChange={onQuarterChange}
						fiscalYearStart={fiscalYearStart}
						onFiscalYearStartChange={onFiscalYearStartChange}
					/>

					{/* Column Count Selector */}
					<div className="flex items-center space-x-2">
						<label htmlFor="column-count" className="text-sm font-medium text-gray-700">
							Columns:
						</label>
						<Select
							id="column-count"
							value={columnCount}
							onChange={e => onColumnCountChange(parseInt(e.target.value))}
							className="w-16"
						>
							<option value={2}>2</option>
							<option value={3}>3</option>
							<option value={4}>4</option>
							<option value={5}>5</option>
							<option value={6}>6</option>
							<option value={7}>7</option>
							<option value={8}>8</option>
						</Select>
						<span className="text-sm text-gray-500">
							showing {Math.min(columnCount, quarters.length)} of {quarters.length}
						</span>
					</div>
				</div>

				<div className="flex items-center space-x-2">
					<Button
						variant="ghost"
						onClick={onAddQuarter}
						disabled={isReadOnly}
					>
						<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
							/>
						</svg>
						Add Qtr
					</Button>

					<Button
						variant="ghost"
						onClick={onAddLineItem}
						disabled={isReadOnly}
					>
						<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 6v6m0 0v6m0-6h6m-6 0H6"
							/>
						</svg>
						Add Item
					</Button>

					<Button
						variant="ghost"
						onClick={onUpload}
						disabled={isReadOnly}
					>
						<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
							/>
						</svg>
						Upload
					</Button>

					<Button
						variant="ghost"
						onClick={onExport}
					>
						<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
							/>
						</svg>
						Export
					</Button>
				</div>
			</div>
		</div>
	)
}
